<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CaptainLlmConfigsAPI from 'dashboard/api/captain/llmConfigs';
import axios from 'axios';
import SectionLayout from '../../account/components/SectionLayout.vue';

const { t } = useI18n();

const configs = ref([]);
const isLoading = ref(false);
const showForm = ref(false);
const remoteModels = ref([]);
const fetchingModels = ref(false);

const formData = ref({
  name: '',
  provider: '',
  endpoint: '',
  api_key: '',
  model_id: '',
  embedding_model: '',
});

const PROVIDERS = [
  { key: 'zhipu', name: '智譜 GLM', endpoint: 'https://open.bigmodel.cn/api/paas/v4/', embedding: 'embedding-3' },
  { key: 'tongyi', name: '通義千問', endpoint: 'https://dashscope.aliyuncs.com/compatible-mode/v1', embedding: 'text-embedding-v3' },
  { key: 'deepseek', name: 'DeepSeek', endpoint: 'https://api.deepseek.com/v1', embedding: '' },
  { key: 'moonshot', name: 'Moonshot (Kimi)', endpoint: 'https://api.moonshot.cn/v1', embedding: '' },
  { key: 'baidu', name: '百度文心', endpoint: 'https://qianfan.baidubce.com/v2', embedding: 'bge-large-zh' },
  { key: 'doubao', name: '字節豆包', endpoint: 'https://ark.cn-beijing.volces.com/api/v3', embedding: 'doubao-embedding-text-240715' },
  { key: 'hunyuan', name: '騰訊混元', endpoint: 'https://api.hunyuan.cloud.tencent.com/v1', embedding: '' },
  { key: 'minimax', name: 'MiniMax', endpoint: 'https://api.minimax.chat/v1', embedding: 'embo-01' },
  { key: 'lingyi', name: '零一萬物', endpoint: 'https://api.lingyiwanwu.com/v1', embedding: '' },
  { key: 'stepfun', name: '階躍星辰', endpoint: 'https://api.stepfun.com/v1', embedding: '' },
  { key: 'baichuan', name: '百川智能', endpoint: 'https://api.baichuan-ai.com/v1', embedding: '' },
  { key: 'siliconflow', name: 'SiliconFlow', endpoint: 'https://api.siliconflow.cn/v1', embedding: 'BAAI/bge-large-zh-v1.5' },
  { key: 'openai', name: 'OpenAI', endpoint: 'https://api.openai.com/v1', embedding: 'text-embedding-3-small' },
  { key: 'anthropic', name: 'Anthropic', endpoint: 'https://api.anthropic.com/v1', embedding: '' },
  { key: 'gemini', name: 'Google Gemini', endpoint: 'https://generativelanguage.googleapis.com/v1beta', embedding: 'text-embedding-004' },
  { key: 'custom', name: '自定義', endpoint: '', embedding: '' },
];

const platformConfigs = computed(() =>
  configs.value.filter(c => c['platform?'] === true || c.platform === true || c.source_label === 'platform')
);
const tenantConfigs = computed(() =>
  configs.value.filter(c => c['platform?'] !== true && c.platform !== true && c.source_label !== 'platform')
);

async function loadConfigs() {
  isLoading.value = true;
  try {
    const res = await CaptainLlmConfigsAPI.get();
    configs.value = res.data;
  } catch (e) {
    // silent
  } finally {
    isLoading.value = false;
  }
}

function onProviderChange() {
  const p = PROVIDERS.find(x => x.key === formData.value.provider);
  if (p) {
    formData.value.endpoint = p.endpoint;
    if (p.embedding) {
      formData.value.embedding_model = p.embedding;
    }
  }
  // Clear previously fetched models when provider changes
  remoteModels.value = [];
}

async function fetchRemoteModels() {
  if (!formData.value.endpoint) {
    useAlert(t('CAPTAIN_SETTINGS.LLM_MANAGER.ERR_NO_ENDPOINT'));
    return;
  }
  fetchingModels.value = true;
  try {
    const res = await CaptainLlmConfigsAPI.fetchModels({
      provider: formData.value.provider,
      endpoint: formData.value.endpoint,
      api_key: formData.value.api_key,
    });
    if (res.data.error) {
      // API returned error but with fallback_models from preset
      if (res.data.fallback_models?.length) {
        remoteModels.value = res.data.fallback_models;
        useAlert(t('CAPTAIN_SETTINGS.LLM_MANAGER.FETCH_FALLBACK'));
      } else {
        useAlert(res.data.error);
      }
    } else {
      remoteModels.value = res.data.models || [];
      if (remoteModels.value.length === 0) {
        useAlert(t('CAPTAIN_SETTINGS.LLM_MANAGER.FETCH_EMPTY'));
      }
    }
  } catch (e) {
    const msg = e.response?.data?.error || e.message;
    useAlert(msg);
  } finally {
    fetchingModels.value = false;
  }
}

async function submitConfig() {
  try {
    await CaptainLlmConfigsAPI.create(formData.value);
    useAlert(t('CAPTAIN_SETTINGS.LLM_MANAGER.ADDED'));
    resetForm();
    await loadConfigs();
  } catch (e) {
    const msgs = e.response?.data?.errors;
    useAlert(msgs ? msgs.join(', ') : t('CAPTAIN_SETTINGS.LLM_MANAGER.ERROR'));
  }
}

async function toggleTenant(config, enabled) {
  try {
    await CaptainLlmConfigsAPI.update(config.id, { enabled });
    config.enabled = enabled;
    useAlert(t('CAPTAIN_SETTINGS.LLM_MANAGER.UPDATED'));
  } catch (e) {
    config.enabled = !enabled;
    useAlert(t('CAPTAIN_SETTINGS.LLM_MANAGER.ERROR'));
  }
}

async function deleteTenant(config) {
  if (!confirm(t('CAPTAIN_SETTINGS.LLM_MANAGER.CONFIRM_DELETE'))) return;
  try {
    await CaptainLlmConfigsAPI.delete(config.id);
    useAlert(t('CAPTAIN_SETTINGS.LLM_MANAGER.DELETED'));
    await loadConfigs();
  } catch (e) {
    useAlert(t('CAPTAIN_SETTINGS.LLM_MANAGER.ERROR'));
  }
}

function resetForm() {
  formData.value = { name: '', provider: '', endpoint: '', api_key: '', model_id: '', embedding_model: '' };
  showForm.value = false;
}

onMounted(() => loadConfigs());
</script>

<template>
  <SectionLayout
    :title="t('CAPTAIN_SETTINGS.LLM_MANAGER.TITLE')"
    :description="t('CAPTAIN_SETTINGS.LLM_MANAGER.DESCRIPTION')"
  >
    <div v-if="isLoading" class="text-sm text-n-slate-11 py-4">
      {{ t('CAPTAIN_SETTINGS.LOADING') }}
    </div>

    <div v-else class="flex flex-col gap-6">
      <!-- Platform Models (read-only) -->
      <div v-if="platformConfigs.length" class="flex flex-col gap-2">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN_SETTINGS.LLM_MANAGER.PLATFORM_MODELS') }}
        </h4>
        <div
          v-for="cfg in platformConfigs"
          :key="cfg.id"
          class="flex items-center justify-between p-3 rounded-lg border border-n-weak bg-n-solid-1"
        >
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <span class="text-[10px] uppercase px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              {{ t('CAPTAIN_SETTINGS.LLM_MANAGER.TAG_PLATFORM') }}
            </span>
            <div class="min-w-0">
              <div class="text-sm font-medium text-n-slate-12 truncate">{{ cfg.name }}</div>
              <div class="text-xs text-n-slate-11 truncate">
                {{ cfg.provider }} · {{ cfg.model_id }}
                <span v-if="cfg.embedding_model"> · {{ cfg.embedding_model }}</span>
              </div>
            </div>
          </div>
          <span class="text-xs" :class="cfg.enabled ? 'text-green-600' : 'text-n-slate-10'">
            {{ cfg.enabled ? t('CAPTAIN_SETTINGS.LLM_MANAGER.ENABLED') : t('CAPTAIN_SETTINGS.LLM_MANAGER.DISABLED') }}
          </span>
        </div>
      </div>

      <!-- Tenant Models (CRUD) -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN_SETTINGS.LLM_MANAGER.TENANT_MODELS') }}
          </h4>
          <button
            type="button"
            class="text-xs px-3 py-1.5 rounded-md bg-woot-500 text-white hover:bg-woot-600"
            @click="showForm = !showForm"
          >
            {{ showForm ? t('CAPTAIN_SETTINGS.LLM_MANAGER.CANCEL') : t('CAPTAIN_SETTINGS.LLM_MANAGER.ADD_BTN') }}
          </button>
        </div>

        <div
          v-for="cfg in tenantConfigs"
          :key="cfg.id"
          class="flex items-center justify-between p-3 rounded-lg border border-n-weak bg-n-solid-1"
        >
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <span class="text-[10px] uppercase px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
              {{ t('CAPTAIN_SETTINGS.LLM_MANAGER.TAG_TENANT') }}
            </span>
            <div class="min-w-0">
              <div class="text-sm font-medium text-n-slate-12 truncate">{{ cfg.name }}</div>
              <div class="text-xs text-n-slate-11 truncate">
                {{ cfg.provider }} · {{ cfg.model_id }}
                <span v-if="cfg.embedding_model"> · {{ cfg.embedding_model }}</span>
              </div>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <label class="flex items-center gap-1 text-xs cursor-pointer">
              <input
                type="checkbox"
                :checked="cfg.enabled"
                @change="toggleTenant(cfg, $event.target.checked)"
              />
            </label>
            <button
              type="button"
              class="text-red-500 hover:text-red-700 text-xs px-2 py-1"
              @click="deleteTenant(cfg)"
            >
              {{ t('CAPTAIN_SETTINGS.LLM_MANAGER.DELETE') }}
            </button>
          </div>
        </div>

        <p v-if="!tenantConfigs.length" class="text-xs text-n-slate-10 italic">
          {{ t('CAPTAIN_SETTINGS.LLM_MANAGER.NO_TENANT_MODELS') }}
        </p>
      </div>

      <!-- Add Form -->
      <div v-if="showForm" class="flex flex-col gap-3 p-4 rounded-lg border border-n-weak bg-n-solid-2">
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="text-xs text-n-slate-11">{{ t('CAPTAIN_SETTINGS.LLM_MANAGER.NAME') }}</label>
            <input v-model="formData.name" type="text" class="w-full mt-1 px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1" />
          </div>
          <div>
            <label class="text-xs text-n-slate-11">{{ t('CAPTAIN_SETTINGS.LLM_MANAGER.PROVIDER') }}</label>
            <select v-model="formData.provider" class="w-full mt-1 px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1" @change="onProviderChange">
              <option value="">{{ t('CAPTAIN_SETTINGS.LLM_MANAGER.SELECT_PROVIDER') }}</option>
              <option v-for="p in PROVIDERS" :key="p.key" :value="p.key">{{ p.name }}</option>
            </select>
          </div>
          <div>
            <label class="text-xs text-n-slate-11">{{ t('CAPTAIN_SETTINGS.LLM_MANAGER.API_KEY') }}</label>
            <input v-model="formData.api_key" type="password" class="w-full mt-1 px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1" />
          </div>
          <div>
            <label class="text-xs text-n-slate-11">{{ t('CAPTAIN_SETTINGS.LLM_MANAGER.ENDPOINT') }}</label>
            <input v-model="formData.endpoint" type="text" class="w-full mt-1 px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1" />
          </div>
          <div>
            <label class="text-xs text-n-slate-11">
              {{ t('CAPTAIN_SETTINGS.LLM_MANAGER.MODEL_ID') }}
            </label>
            <div class="flex gap-1 mt-1">
              <input
                v-model="formData.model_id"
                type="text"
                list="remote-models-list"
                placeholder="glm-4-flash"
                class="flex-1 px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1"
              />
              <button
                type="button"
                :disabled="fetchingModels"
                class="px-2 py-2 text-xs rounded-md border border-n-weak hover:bg-n-solid-3 whitespace-nowrap"
                @click="fetchRemoteModels"
              >
                {{ fetchingModels ? '...' : t('CAPTAIN_SETTINGS.LLM_MANAGER.FETCH_MODELS') }}
              </button>
            </div>
            <datalist id="remote-models-list">
              <option v-for="m in remoteModels" :key="m" :value="m" />
            </datalist>
          </div>
          <div>
            <label class="text-xs text-n-slate-11">{{ t('CAPTAIN_SETTINGS.LLM_MANAGER.EMBEDDING') }}</label>
            <input v-model="formData.embedding_model" type="text" placeholder="embedding-3" class="w-full mt-1 px-3 py-2 text-sm rounded-md border border-n-weak bg-n-solid-1" />
          </div>
        </div>
        <div class="flex justify-end gap-2">
          <button type="button" class="px-4 py-2 text-sm rounded-md border border-n-weak hover:bg-n-solid-3" @click="resetForm">
            {{ t('CAPTAIN_SETTINGS.LLM_MANAGER.CANCEL') }}
          </button>
          <button type="button" class="px-4 py-2 text-sm rounded-md bg-woot-500 text-white hover:bg-woot-600" @click="submitConfig">
            {{ t('CAPTAIN_SETTINGS.LLM_MANAGER.SAVE') }}
          </button>
        </div>
      </div>
    </div>
  </SectionLayout>
</template>
